import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Users, TrendingUp, Plus, Trash2, Loader2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const EVENT_TYPES = [
  { v: 'committee_meeting', l: 'Safety Committee Meeting' },
  { v: 'spi_review', l: 'SPI Review' },
  { v: 'safety_walk', l: 'Safety Walk / Gemba' },
  { v: 'other', l: 'Other Engagement' },
];

export default function AEEngagementPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    event_type: 'committee_meeting',
    title: '',
    date: new Date().toISOString().slice(0, 10),
    ae_attended: true,
    ae_name: '',
    notes: '',
  });
  const token = localStorage.getItem('token');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        fetch(`${API}/api/gaca-p1/ae-engagement/summary`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/gaca-p1/ae-engagement/logs`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setData(s);
      setLogs(l.logs || []);
    } catch (e) { toast.error('Failed to load AE engagement'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      const res = await fetch(`${API}/api/gaca-p1/ae-engagement/logs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success('Engagement logged');
      setOpen(false);
      setForm({ ...form, title: '', notes: '' });
      fetchAll();
    } catch { toast.error('Save failed'); }
  };

  const remove = async (id) => {
    try {
      await fetch(`${API}/api/gaca-p1/ae-engagement/logs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  if (loading || !data) return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-[1400px] mx-auto space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/30">
                <Crown className="w-6 h-6 text-amber-300" />
              </div>
              <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="ae-title">
                {t('aeEngagement.title', 'Accountable Executive Engagement')}
              </h1>
            </div>
            <p className="text-slate-400 max-w-3xl">
              {t('aeEngagement.subtitle', 'Demonstrates AE active and visible leadership in the SMS — committee attendance, SPI reviews, and engagement events. Maps to GACA item 1.1.4.')}
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-500" data-testid="ae-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Log Engagement
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="ae-dialog">
              <DialogHeader><DialogTitle>Log AE Engagement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Event type</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                    <SelectTrigger data-testid="ae-event-type" className="bg-slate-950 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVENT_TYPES.map(e => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title / topic</Label>
                  <Input data-testid="ae-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-slate-950 border-slate-800" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input data-testid="ae-date" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-slate-950 border-slate-800" />
                </div>
                <div>
                  <Label>AE name (optional)</Label>
                  <Input data-testid="ae-name" value={form.ae_name} onChange={e => setForm({ ...form, ae_name: e.target.value })} className="bg-slate-950 border-slate-800" />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Checkbox data-testid="ae-attended" checked={form.ae_attended} onCheckedChange={(v) => setForm({ ...form, ae_attended: !!v })} />
                  <Label>AE attended / participated</Label>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea data-testid="ae-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="bg-slate-950 border-slate-800" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="bg-amber-600 hover:bg-amber-500" data-testid="ae-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader><CardTitle className="text-slate-300 text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4 text-sky-400" /> Committee meetings ({data.window_months}m)</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white tabular-nums" data-testid="ae-meetings-total">{data.committee_meetings.total}</div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-slate-400">AE attended</span>
                <span className="text-emerald-300 tabular-nums" data-testid="ae-attendance-pct">{data.committee_meetings.ae_attended} ({data.committee_meetings.attendance_pct}%)</span>
              </div>
              <Progress value={data.committee_meetings.attendance_pct} className="h-1.5 mt-2" />
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader><CardTitle className="text-slate-300 text-sm font-medium flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> Manual engagement logs</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white tabular-nums" data-testid="ae-manual-total">{data.manual_engagement_logs.total}</div>
              <div className="mt-2 text-sm text-slate-400">{data.manual_engagement_logs.ae_attended} with AE</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader><CardTitle className="text-slate-300 text-sm font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> SPI reviews</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white tabular-nums" data-testid="ae-spi-reviews">{data.spi_reviews}</div>
              <div className="mt-2 text-sm text-slate-400">Updated in window</div>
            </CardContent>
          </Card>
        </div>

        {/* Logs table */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader><CardTitle className="text-white text-lg">Engagement Log</CardTitle></CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center" data-testid="ae-empty">No engagement logs yet. Click "Log Engagement" to add the first entry.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 border-b border-slate-800">
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Type</th>
                      <th className="text-left p-2">Title</th>
                      <th className="text-left p-2">AE</th>
                      <th className="text-left p-2">Notes</th>
                      <th className="text-right p-2">—</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id} className="border-b border-slate-800/60 hover:bg-slate-800/40" data-testid={`ae-row-${l.id}`}>
                        <td className="p-2 text-slate-200 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1 text-slate-500" />{l.date}</td>
                        <td className="p-2"><Badge className="bg-slate-800 text-slate-300 border border-slate-700">{(EVENT_TYPES.find(e => e.v === l.event_type)?.l) || l.event_type}</Badge></td>
                        <td className="p-2 text-slate-200">{l.title}</td>
                        <td className="p-2">
                          {l.ae_attended
                            ? <Badge className="bg-emerald-500/15 border-emerald-500/40 text-emerald-300">Attended</Badge>
                            : <Badge className="bg-slate-700/40 border-slate-700 text-slate-400">Not present</Badge>}
                        </td>
                        <td className="p-2 text-slate-400 max-w-md truncate">{l.notes}</td>
                        <td className="p-2 text-right">
                          <Button variant="ghost" size="sm" onClick={() => remove(l.id)} className="text-rose-400 hover:text-rose-300" data-testid={`ae-delete-${l.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
